import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ensurePushSubscription } from "@/lib/webPush";
import ThanksOverlay from "@/components/ThanksOverlay";
import PushEnableBanner from "@/components/PushEnableBanner";
import InstallPrompt from "@/components/InstallPrompt";
import PeriodTabs from "@/components/PeriodTabs";
import IosInstallHint from "@/components/IosInstallHint";
import Brand from "@/components/Brand";
import UtrSearch from "@/components/UtrSearch";
import BalanceNotification from "@/components/BalanceNotification";
import NotificationsBell from "@/components/NotificationsBell";
import UpdateBanner from "@/components/UpdateBanner";
import BroadcastGate from "@/components/BroadcastGate";
import { House, ChartBar, UploadSimple, Gear, SignOut } from "@phosphor-icons/react";

// v1.27.67 — Customer portal navigation collapses to exactly 4 tabs
// (Home / Reports / Uploads / Settings). On phones these render as a
// fixed bottom nav; on desktop they remain as top nav pills. Detail
// pages (/portal/ledger, /portal/monthly-summary, /portal/upload,
// /portal/my-uploads, /portal/notifications) still exist as sub-routes
// and land under their parent tab.
const nav = [
    { to: "/portal",         end: true, label: "Home",     icon: House,        testId: "nav-portal-home" },
    { to: "/portal/reports",            label: "Reports",  icon: ChartBar,     testId: "nav-portal-reports" },
    { to: "/portal/uploads",            label: "Uploads",  icon: UploadSimple, testId: "nav-portal-uploads" },
    { to: "/portal/settings",           label: "Settings", icon: Gear,         testId: "nav-portal-settings" },
];

export default function CustomerLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [thanksOpen, setThanksOpen] = useState(false);
    const [thanksKind, setThanksKind] = useState("slip");
    const [thanksMeta, setThanksMeta] = useState(null);

    useEffect(() => {
        if (!user || user.role !== "customer") return;
        const t = setTimeout(() => { ensurePushSubscription({ silent: false }).catch(() => {}); }, 1200);
        return () => clearTimeout(t);
    }, [user]);

    useEffect(() => {
        if (searchParams.get("thanks") === "1") {
            setThanksOpen(true);
            const p = new URLSearchParams(searchParams);
            p.delete("thanks");
            setSearchParams(p, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        const onThanks = (e) => {
            const detail = (e && e.detail) || {};
            setThanksKind(detail.kind || "slip");
            setThanksMeta(detail.meta || null);
            setThanksOpen(true);
        };
        window.addEventListener("micm-thanks-fire", onThanks);
        return () => window.removeEventListener("micm-thanks-fire", onThanks);
    }, []);

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    return (
        <div className="min-h-screen bg-slate-50">
            <UpdateBanner />
            <BroadcastGate />
            <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/85 backdrop-blur-md">
                <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
                    <Link to="/portal" data-testid="brand-logo" className="flex items-center gap-3">
                        <img
                            src="/logo192.png"
                            alt="MD Impex India"
                            className="logo-spin-glow h-10 w-10 shrink-0 rounded-md object-contain bg-black ring-1 ring-yellow-500/30"
                            data-testid="brand-logo-img-customer"
                        />
                        <Brand size="sm" shimmer sub="Customer Portal" />
                    </Link>
                    <nav className="hidden items-center gap-1 md:flex" data-testid="customer-top-nav">
                        {nav.map(({ to, end, label, icon: Icon, testId }) => (
                            <NavLink
                                key={to}
                                to={to}
                                end={end}
                                data-testid={testId}
                                className={({ isActive }) =>
                                    `flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors duration-200 ${
                                        isActive
                                            ? "bg-slate-900 text-white"
                                            : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                                    }`
                                }
                            >
                                <Icon size={16} />
                                <span>{label}</span>
                            </NavLink>
                        ))}
                    </nav>
                    <div className="flex items-center gap-2">
                        {/* Desktop: inline UTR search in the header. Mobile: rendered
                            full-width in a dedicated row below (see next block). */}
                        <UtrSearch className="hidden sm:block" />
                        <NotificationsBell viewAllHref="/portal/notifications" />
                        <div className="hidden text-right lg:block">
                            <div className="text-sm font-medium text-slate-900">{user?.name}</div>
                            <div className="text-xs text-slate-500">{user?.email || user?.phone}</div>
                        </div>
                        <button
                            data-testid="logout-btn"
                            onClick={handleLogout}
                            className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-100"
                        >
                            <SignOut size={16} />
                            <span className="hidden sm:inline">Sign out</span>
                        </button>
                    </div>
                </div>
                {/* v1.28.19 — Mobile-only full-width UTR search row. The
                    header pill (above) is hidden on <sm screens where there
                    is no room for it; this row makes UTR search reachable
                    on every phone size (backend `/uploads/search` is
                    already scoped to the signed-in party's own slips). */}
                <div className="border-t border-slate-100 bg-white/85 px-4 py-2 sm:hidden" data-testid="customer-mobile-utr-row">
                    <UtrSearch className="w-full" />
                </div>
            </header>

            {/* v1.27.67 — main content: extra bottom padding on mobile so the
                fixed bottom-nav doesn't cover the last content row. */}
            <main className="mx-auto max-w-6xl px-4 pb-28 pt-6 sm:px-6 sm:pb-10 sm:pt-8">
                <PushEnableBanner />
                <InstallPrompt />
                <PeriodTabs />
                <Outlet />
            </main>

            {/* v1.27.67 — Fixed bottom nav on phones. Exactly 4 tabs. */}
            <nav
                data-testid="customer-bottom-nav"
                className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 shadow-[0_-4px_16px_-8px_rgba(15,23,42,0.15)] backdrop-blur md:hidden"
                style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
            >
                <div className="mx-auto grid max-w-6xl grid-cols-4">
                    {nav.map(({ to, end, label, icon: Icon, testId }) => (
                        <NavLink
                            key={to}
                            to={to}
                            end={end}
                            data-testid={`${testId}-mobile`}
                            className={({ isActive }) =>
                                `flex flex-col items-center justify-center gap-1 py-2.5 text-[11px] font-semibold transition-colors ${
                                    isActive
                                        ? "text-yellow-600"
                                        : "text-slate-500 hover:text-slate-800"
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    <Icon size={22} weight={isActive ? "fill" : "regular"} />
                                    <span className="tracking-wide">{label}</span>
                                </>
                            )}
                        </NavLink>
                    ))}
                </div>
            </nav>

            <BalanceNotification />
            <IosInstallHint />
            <ThanksOverlay open={thanksOpen} onClose={() => setThanksOpen(false)} kind={thanksKind} meta={thanksMeta} />
        </div>
    );
}
